// Screen 5 - route card (slice-2-onboarding.md §8; extends mockup A2,
// docs/design/mockups/02-app.html:149-203). Fingerprint panel + station chips +
// environment pills (locked required pill, no toggle interaction possible) + one-tap
// confirm + post-confirm handoff. Frontend/backend/database fingerprint rows always read
// "Not detected yet" this slice (the honesty gap, §8.2: the Slice-2 stub only derives
// hasPayments/hasAuth/hasPii, never a real stack scan).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { EnvironmentType } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper } from "../../components/Stepper.js";
import { EnvironmentPills } from "../../components/EnvPill.js";
import { getRouteCard, confirmRoute } from "../../api/route.js";
import type { RouteCardResponse } from "../../api/route.js";
import { useWizard } from "../../state/wizard.js";
import { formatFloorReason } from "./floor-reason.js";

const STATION_LABEL: Record<string, string> = {
  baseline_audit: "Baseline audit",
  certify: "Certify",
  build: "Build",
  production_build: "Production Build",
  migration: "Migration",
  run: "Run",
};

type ScreenState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "ready"; readonly data: RouteCardResponse }
  | { readonly kind: "confirming"; readonly data: RouteCardResponse }
  | { readonly kind: "confirm_failed"; readonly data: RouteCardResponse }
  | { readonly kind: "confirmed" };

export const RouteCard = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft } = useWizard();
  const [state, setState] = useState<ScreenState>({ kind: "loading" });

  useEffect(() => {
    const { appId } = draft;
    if (appId === null) return;
    const cancelled = { current: false };
    void (async () => {
      const result = await getRouteCard(appId);
      if (cancelled.current) return;
      setState(isErr(result) ? { kind: "error" } : { kind: "ready", data: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [draft.appId]);

  const handleConfirm = async (data: RouteCardResponse): Promise<void> => {
    if (draft.appId === null) return;
    setState({ kind: "confirming", data });
    const result = await confirmRoute(draft.appId);
    if (isErr(result)) {
      setState({ kind: "confirm_failed", data });
      return;
    }
    setState({ kind: "confirmed" });
  };

  const handleToggle = (type: EnvironmentType, nextEnabled: boolean): void => {
    setState((prev) => {
      if (prev.kind !== "ready" && prev.kind !== "confirm_failed") return prev;
      const environments = prev.data.recommendation.environments.map((env) =>
        env.type === type && env.toggleable ? { ...env, enabled: nextEnabled } : env,
      );
      return {
        ...prev,
        data: { ...prev.data, recommendation: { ...prev.data.recommendation, environments } },
      };
    });
  };

  if (state.kind === "loading") {
    return (
      <FocusedFlowShell
        title="Confirm your route"
        onBack={() => {
          void navigate("/apps/new/cloud");
        }}
      >
        <OuterStepper active="confirm_route" />
        <div className="grid mt24" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div className="card pad skeleton-card" />
          <div className="card pad skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "error") {
    return (
      <FocusedFlowShell
        title="Confirm your route"
        onBack={() => {
          void navigate("/apps/new/cloud");
        }}
      >
        <div className="card state-card">
          <p>We could not build your route recommendation. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setState({ kind: "loading" });
            }}
          >
            Retry
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "confirmed") {
    return (
      <FocusedFlowShell
        title="Confirm your route"
        onBack={() => {
          void navigate("/apps");
        }}
      >
        <div className="card pad" role="status">
          <p>
            Route confirmed. Baseline audit queued - your knowledge doc and risk list will be ready
            soon.
          </p>
          <button
            type="button"
            className="btn btn-primary mt12"
            onClick={() => {
              void navigate("/apps/new/sdk");
            }}
          >
            Continue to SDK install
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { data } = state;
  const floorReason = formatFloorReason(data.fingerprint);

  return (
    <FocusedFlowShell
      title="Confirm your route"
      onBack={() => {
        void navigate("/apps/new/cloud");
      }}
      cta={
        <button
          type="button"
          className="btn btn-primary btn-lg"
          disabled={state.kind === "confirming"}
          onClick={() => {
            void handleConfirm(data);
          }}
        >
          {state.kind === "confirming" ? "Confirming..." : "Confirm route and start baseline audit"}
        </button>
      }
    >
      <OuterStepper active="confirm_route" />
      {state.kind === "confirm_failed" ? (
        <p className="field-error" role="alert">
          That did not go through. Try again.
        </p>
      ) : null}
      <div className="grid mt16" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div className="card pad">
          <div className="h3">What we detected</div>
          <p className="muted" style={{ fontSize: 13 }}>
            From your connected repo. Nothing was changed.
          </p>
          <dl className="mt16">
            <div className="fp-row">
              <dt className="muted">Frontend</dt>
              <dd style={{ margin: 0 }}>
                <b>Not detected yet - the full scan runs after your baseline audit</b>
              </dd>
            </div>
            <div className="fp-row">
              <dt className="muted">Backend</dt>
              <dd style={{ margin: 0 }}>
                <b>Not detected yet - the full scan runs after your baseline audit</b>
              </dd>
            </div>
            <div className="fp-row">
              <dt className="muted">Database</dt>
              <dd style={{ margin: 0 }}>
                <b>Not detected yet - the full scan runs after your baseline audit</b>
              </dd>
            </div>
            <div className="fp-row">
              <dt className="muted">Payments</dt>
              <dd style={{ margin: 0 }}>
                <b>{data.fingerprint.hasPayments ? "Detected" : "Not detected"}</b>
              </dd>
            </div>
          </dl>
        </div>

        <div className="stack gap16">
          <div className="card pad">
            <div className="eyebrow">Recommended route</div>
            <ol
              className="row aic gap8 mt8"
              style={{ flexWrap: "wrap", listStyle: "none", padding: 0, margin: "var(--space-5) 0 0" }}
              aria-label="Recommended route"
            >
              {data.recommendation.stations.map((station, index) => (
                <li key={station} className="row aic gap8" style={{ display: "inline-flex" }}>
                  <span
                    className="chip"
                    style={
                      index === data.recommendation.stations.length - 1
                        ? { borderColor: "var(--amber-line)", background: "var(--amber-soft)" }
                        : undefined
                    }
                  >
                    {STATION_LABEL[station] ?? station}
                  </span>
                  {index < data.recommendation.stations.length - 1 ? (
                    <span className="muted" aria-hidden="true">
                      →
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
          <div className="card pad">
            <div className="eyebrow">Environments Nightcrew recommends</div>
            <div className="mt12">
              <EnvironmentPills
                environments={data.recommendation.environments}
                floorReason={floorReason}
                onToggle={handleToggle}
              />
            </div>
            {floorReason !== null ? (
              <p className="muted mt12" style={{ fontSize: 12.5 }}>
                Held on because {floorReason}.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </FocusedFlowShell>
  );
};
